import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { IconHeader } from "../../../../../../Icon/IconHader/IconHeader";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import {
  createSanctions,
  createCompensations,
  catalogByPart,
} from "../../../../../../../apis";
import { useSnackbar } from "notistack";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";
import { GenerateWithIA } from "../../../../Investigation2/components";
import { SanctionCompensationAISuggestion } from "./SanctionCompensationAISugestion";
import { UnsavedChanges } from "../../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { useReportId } from "../../../../../../../context/report/ReportContext";

const SANCTIONS_CATALOGUE = "RS-102103";

export const SanctionCompensationForm = ({
  closeForm,
  type,
  people,
  reloadInfo,
  reportRender,
}) => {
  // Custom Hooks
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { sanctionsOptions, benefitOptions, involvedType, titlesType } =
    useReportContext();
  const theme = useTheme();
  const [t] = useTranslation("report");
  const lang = localStorage.getItem("lang") || "es";

  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  // Person selected
  const [involvedSelected, setInvolvedSelected] = useState({});
  // Selected actions
  const [selectedActions, setSelectedActions] = useState([]);
  // Add another action
  const [another, setAnother] = useState([]);
  // Catalog state for sanctions
  const [catalogSanctionsOpts, setCatalogSanctionsOpts] = useState(null);
  const isOffenders = type === "offenders";
  const useCatalog = isOffenders && Array.isArray(catalogSanctionsOpts);

  // Consultar catálogo de sanciones solo para offenders
  useEffect(() => {
    if (!isOffenders) return;
    const fetchCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: false,
          catalogue: SANCTIONS_CATALOGUE,
          path: "/",
        });
        if (!resp.error && Array.isArray(resp.data) && resp.data.length > 0) {
          const capitalize = (str) =>
            str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
          setCatalogSanctionsOpts(
            resp.data.map((item) => ({
              label: capitalize(item.locales?.[lang] || item.label),
              value: item.path_locales,
              locales: item.locales || {},
            })),
          );
        }
      } catch {
        // Si falla, se usan las opciones hardcodeadas
      }
    };
    fetchCatalog();
  }, []);
  // Sanction date
  const [determinationDate, setDeterminationDate] = useState(null);
  // Execution date
  const [executionDate, setExecutionDate] = useState(null);
  // Add comment
  const [addComment, setAddComment] = useState(false);
  // General comment about sanction
  const [comment, setComment] = useState("");
  // Generate sanction with AI
  const [AIGeneration, setAIGeneration] = useState(false);
  // Show unsaved changes confirmation modal
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // The form type is determined
  const property =
    type === "offenders" ? "another_sanction" : "another_benefit";

  // Clean form
  const resetForm = () => {
    setInvolvedSelected("");
    setSelectedActions([]);
    setAnother([]);
    setDeterminationDate(null);
    setExecutionDate(null);
    setAddComment(false);
    setComment("");
  };

  // Detecta si el usuario capturó algo en el formulario
  const hasChanges =
    !!involvedSelected?.value ||
    selectedActions.length > 0 ||
    another.length > 0 ||
    !!determinationDate ||
    !!executionDate ||
    comment.trim() !== "";

  // Close & clean selected form
  const hdlCancel = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
      return;
    }
    resetForm();
    closeForm();
  };

  // Confirmar salida descartando los cambios
  const hdlConfirmLeave = () => {
    setShowUnsavedModal(false);
    resetForm();
    closeForm();
  };

  // Add another sanction
  const hdlAddAnotherAction = () => {
    setTimeout(
      () =>
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        }),
      100,
    );
    setAnother((prev) => [...prev, { id: crypto.randomUUID(), [property]: "" }]);
  };

  // Add another action
  const hdlInputAnotherActionChange = (index, value) => {
    setAnother((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [property]: value } : item,
      ),
    );
  };

  // Delete another action
  const hdlDeleteAnotherAction = (index) => {
    setAnother(another.filter((_, i) => i !== index));
  };

  // Validation data
  const hasEmptyAnother = () =>
    another.some((item) => item[property]?.trim() === "");

  const isInvalid =
    !involvedSelected ||
    (!selectedActions.length && !another.length) ||
    hasEmptyAnother();

  const isAnotherOptionInvalid = hasEmptyAnother();

  // Select involved party
  const hdlChangePersonSelected = (e) => {
    const selectedPerson = people.find(
      (option) => option.value === e.target.value,
    );
    setInvolvedSelected(selectedPerson);
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  // Options type (usa catálogo si existe para offenders)
  const OPTIONS_MAP = {
    offenders: useCatalog ? catalogSanctionsOpts : sanctionsOptions,
    victims: benefitOptions,
  };
  const options = OPTIONS_MAP[type] || [];

  // Sets the value in an array []
  const hdlActionsChange = (_, newValue) => {
    if (useCatalog) {
      // Guardar value, label y locales para no hacer consulta al mostrar
      setSelectedActions(
        newValue.map((option) => ({
          value: option.value,
          label: option.label,
          locales: option.locales || {},
        })),
      );
    } else {
      setSelectedActions(newValue.map((option) => option.value));
    }
  };

  // Send info
  const onSubmitActions = async () => {
    setIsLoading(true);
    const { data } = involvedSelected;
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const PAYLOAD_KEYS = {
      offenders: "sanctions",
      victims: "benefits",
    };

    const API_CALLS = {
      offenders: createSanctions,
      victims: createCompensations,
    };

    const key = PAYLOAD_KEYS[type] || null;
    const endpoint = API_CALLS[type] || null;

    const dataToSend = {
      report_identifier: id,
      ...data,
      ...(selectedActions.length > 0 && key ? { [key]: selectedActions } : {}),
      ...(another.length > 0 && {
        another: another.map(({ id: _id, ...rest }) => rest),
      }),
      comment,
      determination_date: determinationDate
        ? determinationDate.format("YYYY-MM-DD")
        : null,
      execution_date: executionDate ? executionDate.format("YYYY-MM-DD") : null,
    };

    if (!endpoint) {
      console.error("No API function available for type:", type);
      closeSnackbar(sending);
      setIsLoading(false);
      return;
    }

    try {
      await endpoint(dataToSend);
      reloadInfo();
      closeForm();
      setInvolvedSelected("");
      setSelectedActions([]);
      setAnother([]);
      setDeterminationDate(null);
      setExecutionDate(null);
      setAddComment(false);
      setComment("");
      reportRender();
      enqueueSnackbar(`${t("Resolution.data_successfully_updated")}`, {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  return (
    <Box>
      {/* AI Suggestion harcoded don't erase */}
      {/* <SanctionCompensationAISuggestion
        open={AIGeneration}
        onClose={() => setAIGeneration(false)}
        title={`${titlesType[type].generate} ${t("Resolution.for")} ${
          involvedSelected.value
        }`}
        btnTitle={titlesType[type].generate}
        involvedSelected={involvedSelected}
        type={type}
        options={options}
        t={t}
        onApplySuggestion={({ actions, another, details }) => {
          setSelectedActions((prev) =>
            Array.from(new Set([...(prev || []), ...(actions || [])]))
          );
          setAnother(another || []);
          setComment(details || "");
        }}
      /> */}

      <IconHeader title={titlesType[type].title} />
      <EGBasicSelect
        label={`${titlesType[type].selectLabel}:`}
        options={people}
        value={involvedSelected?.value || ""}
        onChange={hdlChangePersonSelected}
        sx={{ mt: 1.5 }}
      />

      {/* Info involved party selected */}
      {involvedSelected.value !== undefined && (
        <>
          <Paper
            sx={{
              my: 1.5,
              boxShadow: `0 0 4px 0 ${theme.palette.blueGray500.main}`,
              width: "100%",
              background: theme.palette.background.paper,
            }}
            elevation={0}
          >
            <ListItem dense>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: "transparent" }}>
                  <EGIconButton
                    iconName={
                      involvedSelected.data.confirm
                        ? "outline_profile_tick"
                        : "outline_profile_remove"
                    }
                    color={
                      involvedSelected.data.confirm
                        ? "limePunch"
                        : "blueGray500"
                    }
                    title={
                      involvedSelected.data.confirm
                        ? t("Resolution.confirmed")
                        : t("Resolution.unconfirmed")
                    }
                    colorHover={
                      involvedSelected.data.confirm ? "greenII" : "blueGray700"
                    }
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="h6" textAlign="center">
                    {involvedSelected.label}
                  </Typography>
                }
                secondary={
                  <Stack
                    spacing={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-around"
                    flexDirection="row"
                    flexWrap="wrap"
                    py={0.5}
                    component={"span"}
                  >
                    <Typography variant="body2" component="span">
                      {
                        involvedType[
                          involvedSelected.data.kind ||
                            involvedSelected.data.rol
                        ]
                      }
                    </Typography>
                    {involvedSelected.data.additional && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.employee_number",
                      )}: ${involvedSelected.data.additional}`}</Typography>
                    )}
                    {involvedSelected.data.relationship && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.job_position",
                      )}: ${involvedSelected.data.relationship}`}</Typography>
                    )}
                    {involvedSelected.data.details && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.area",
                      )}: ${involvedSelected.data.details}`}</Typography>
                    )}
                  </Stack>
                }
              />
            </ListItem>
          </Paper>
          <Stack spacing={1} mt={1.5} direction={{ xs: "column", sm: "row" }}>
            <EGDatePicker
              label={`${t("Resolution.determination_date")}:`}
              value={determinationDate}
              onChange={(date) => setDeterminationDate(date)}
            />
            <EGDatePicker
              label={`${t("Resolution.execution_date")}:`}
              value={executionDate}
              onChange={(date) => setExecutionDate(date)}
            />
          </Stack>
          {/* <Stack mt={1.5} alignItems={"flex-end"}>
            <GenerateWithIA
              text={`${titlesType[type].generate_with_AI}`}
              buttonText={t("Resolution.generate")}
              onClick={() => setAIGeneration(true)}
              isLoading={isLoading}
            />
          </Stack> */}
          <Autocomplete
            multiple
            id={`assign-${type}`}
            options={options}
            disableCloseOnSelect
            getOptionLabel={(option) => option.label}
            sx={{ width: "100%", mx: "auto", mt: 1.5 }}
            value={options.filter((option) =>
              useCatalog
                ? selectedActions.some((a) => a.value === option.value)
                : selectedActions.includes(option.value),
            )}
            onChange={hdlActionsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  sx={{
                    backgroundColor: theme.palette.blueGray100.main,
                    fontSize: 12,
                  }}
                  {...getTagProps({ index })}
                  label={
                    <Stack
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection="row"
                      flexWrap="nowrap"
                    >
                      {option.label}
                    </Stack>
                  }
                />
              ))
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option, { selected }) => {
              return (
                <Box
                  {...props}
                  key={option.value}
                  sx={{
                    maxHeight: 47,
                    p: 2,
                    my: 0.5,
                    mx: 1,
                    borderRadius: 1.5,
                    border: `${theme.palette.blueGray100.main} 1px solid`,
                  }}
                >
                  <Checkbox size="small" checked={selected} />
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <EGInput
                {...params}
                label={`${titlesType[type].inputLabel}:`}
                placeholder={titlesType[type].inputPlaceholder}
              />
            )}
          />
          {/* Another sanctions/remunerations */}
          {another && (
            <Box maxHeight={"25vh"} overflow={"auto"} mt={0.75} py={0.5}>
              {another.map((action, i) => (
                <Stack
                  key={action.id}
                  display="flex"
                  spacing={0.5}
                  alignItems="start"
                  flexDirection="row"
                  flexWrap="nowrap"
                  mt={1.33}
                >
                  <EGInput
                    label={`${titlesType[type].addActionInput} #${i + 1}:`}
                    placeholder={titlesType[type].addOtherActionPlaceholder}
                    fullWidth
                    value={action[property] || ""}
                    onChange={(e) =>
                      hdlInputAnotherActionChange(i, e.target.value)
                    }
                    required
                    autoFocus
                    helperText={titlesType[type].addOtherActionHelperText}
                  />
                  <EGIconButton
                    iconName="close_icon"
                    iconColor="error"
                    onClick={() => hdlDeleteAnotherAction(i)}
                    title={titlesType[type].deleteAction}
                  />
                </Stack>
              ))}
            </Box>
          )}
          {/* Add another action button */}
          <Stack
            display="flex"
            alignItems="center"
            justifyContent="end"
            flexDirection="row"
            mt={0.5}
            spacing={2}
          >
            <EGIconButton
              iconName="add_square"
              iconColor="primary"
              title={titlesType[type].addAnotherAction}
              onClick={hdlAddAnotherAction}
              disabled={isAnotherOptionInvalid}
            />
            {/* Add comment?  */}
            {(!addComment || comment.trim() === "") && (
              <Link onClick={() => setAddComment(true)} underline="hover">
                + {t("Care.add_comment")}
              </Link>
            )}
          </Stack>

          {(addComment || comment.trim() !== "") && (
            <EGInput
              label={`${t("Resolution.details")}: ${comment.length}/500`}
              placeholder={titlesType[type].commentPlaceholder}
              sx={{ mt: 0.5 }}
              multiline
              rows={3}
              value={comment}
              autoFocus
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          )}
        </>
      )}

      <Stack
        display="flex"
        alignItems="center"
        justifyContent="end"
        flexDirection="row"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        spacing={2}
        mt={1.5}
        mb={1}
      >
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitActions}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCancel}
          loading={isLoading}
        />
      </Stack>

      <UnsavedChanges
        open={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onCancel={() => setShowUnsavedModal(false)}
        onConfirm={hdlConfirmLeave}
      />
    </Box>
  );
};
